import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { CardRow } from '../../../../../../generic/ui/CardRow'
import { Mono } from '../../../../../../generic/ui/Mono'
import IconTextField from '../../../../../../generic/ui/Input/IconTextField'
import BarcodeIcon from '../../../../../../generic/icons/IconLib/BarcodeIcon'
import PackageBoxIcon from '../../../../../../generic/icons/IconLib/PackageBoxIcon'
import PalletIcon from '../../../../../../generic/icons/IconLib/PalletIcon'
import ScaleIcon from '../../../../../../generic/icons/IconLib/ScaleIcon'
import CalendarIcon from '../../../../../../generic/icons/IconLib/CalendarIcon'
import AcceptIcon from '../../../../../../generic/icons/IconLib/CheckIcon'
import CloseIcon from '../../../../../../generic/icons/IconLib/XIcon'
import DivideIcon from '../../../../../../generic/icons/IconLib/DivideIcon'
import { IEntriesItem } from '../../../../../../modules/API/Miun/models/sortingList'
import { convertToDate } from '../../../../../../modules/convertDateString'

interface Props {
    item: IEntriesItem
    isLast?: boolean
    moveItem: (item: IEntriesItem, given?: boolean) => void
    selectForDivide: (item: IEntriesItem) => void
    changeWeight: (item: IEntriesItem, qty: number) => void
}

const EntryAvailableCard: React.FC<Props> = ({
    item,
    isLast = false,
    moveItem,
    selectForDivide,
    changeWeight,
}) => {
    const [gross, setGross] = useState<string>(String(item.Gross ?? 0))

    useEffect(() => {
        setGross(String(item.Gross ?? 0))
    }, [item.Gross])

    const pack = `${item.Pack_Name?.trim() ?? ''}${item.AmountPack ? ` · ${item.AmountPack}` : ''}`
    const canDivide = !item.Given && item.AmountPack > 1
    const entryDate = item.DateEntry ? convertToDate(item.DateEntry) : ''

    const handleWeightBlur = () => {
        const n = Number(gross)
        if (!Number.isFinite(n) || n === item.Gross) return
        changeWeight(item, Math.round(n))
    }

    return (
        <CardRow
            $columns='6.5rem minmax(0, 1fr) 7rem 4.75rem'
            $mobileColumns='auto minmax(0, 1fr) 5rem 4.75rem'
            $isLast={isLast}
            $hover={false}
        >
            <CardRow.Cell>
                <CardRow.Stack $gap='0.125rem'>
                    <CardRow.Line $gap='0.375rem'>
                        <IdIcon>
                            <BarcodeIcon color='currentColor' size={16} />
                        </IdIcon>
                        <Id>{item.EntryDetailNum}</Id>
                    </CardRow.Line>
                    {entryDate && (
                        <CardRow.Line $gap='0.25rem'>
                            <SubIcon>
                                <CalendarIcon color='currentColor' size={12} />
                            </SubIcon>
                            <EntryDate>{entryDate}</EntryDate>
                        </CardRow.Line>
                    )}
                </CardRow.Stack>
            </CardRow.Cell>

            <CardRow.Cell>
                <CardRow.Stack $gap='0.25rem'>
                    <CardRow.Line $gap='0.5rem'>
                        <SubIcon>
                            <PackageBoxIcon color='currentColor' size={14} />
                        </SubIcon>
                        <PrimaryName>{pack}</PrimaryName>
                    </CardRow.Line>
                    <CardRow.Line $gap='0.5rem'>
                        <SubIcon>
                            <PalletIcon color='currentColor' size={14} />
                        </SubIcon>
                        <SecondaryName>{item.AmountPallets ?? 0}</SecondaryName>
                    </CardRow.Line>
                </CardRow.Stack>
            </CardRow.Cell>

            <CardRow.Cell $align='center'>
                <WeightField
                    type='number'
                    value={gross}
                    onChange={(e) => setGross(e.target.value)}
                    onBlur={handleWeightBlur}
                    leadingIcon={<ScaleIcon color='currentColor' size={14} />}
                    inputMode='decimal'
                />
            </CardRow.Cell>

            <CardRow.Cell $align='center'>
                <Actions>
                    {canDivide ? (
                        <Pill
                            type='button'
                            $variant='divide'
                            aria-label='divide'
                            onClick={() => selectForDivide(item)}
                        >
                            <DivideIcon />
                        </Pill>
                    ) : (
                        <PillSlot aria-hidden />
                    )}
                    {!item.Given ? (
                        <Pill
                            type='button'
                            $variant='accept'
                            aria-label='accept'
                            onClick={() => moveItem(item, false)}
                        >
                            <AcceptIcon />
                        </Pill>
                    ) : (
                        <Pill
                            type='button'
                            $variant='close'
                            aria-label='remove'
                            onClick={() => moveItem(item, true)}
                        >
                            <span style={{ display: 'inline-flex', cursor: 'pointer' }} onClick={undefined}><CloseIcon /></span>
                        </Pill>
                    )}
                </Actions>
            </CardRow.Cell>
        </CardRow>
    )
}

export default EntryAvailableCard

const IdIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink2};
    flex-shrink: 0;
`
const SubIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink3};
    flex-shrink: 0;
`
const Id = styled(Mono)`
    font-size: ${({ theme }) => theme.text.md};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};
    white-space: nowrap;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`
const EntryDate = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink4};
    white-space: nowrap;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`
const PrimaryName = styled.span`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 500;
    font-family: ${({ theme }) => theme.fonts.sans};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`
const SecondaryName = styled.span`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    font-family: ${({ theme }) => theme.fonts.sans};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`

const WeightField = styled(IconTextField)`
    min-height: 2.25rem;
    input {
        font-family: ${({ theme }) => theme.fonts.mono};
        font-size: ${({ theme }) => theme.text.md};
        text-align: center;
    }
`

const Actions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
`

const PillSlot = styled.span`
    display: inline-block;
    width: 2rem;
    height: 2rem;
    flex-shrink: 0;
`

const Pill = styled.button<{ $variant: 'accept' | 'close' | 'divide' }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    border-radius: 0.6rem;
    cursor: pointer;
    transition: all ${({ theme }) => theme.motion.fast} ease;

    ${({ $variant, theme }) =>
        $variant === 'accept'
            ? `background: ${theme.colors.accent}; color: ${theme.colors.ink}; border: 1px solid ${theme.colors.accent};`
            : `background: ${theme.colors.line2}; color: ${theme.colors.ink2}; border: 1px solid ${theme.colors.line};`}

    svg {
        width: 0.9rem; height: 0.9rem;
    }

    &:hover {
        ${({ $variant, theme }) =>
        $variant === 'accept'
            ? `background: ${theme.colors.accentDeep}; border-color: ${theme.colors.accentDeep};`
            : $variant === 'close'
                ? `background: ${theme.colors.danger}; color: #fff; border-color: ${theme.colors.danger};`
                : `background: ${theme.colors.accentSoft}; color: ${theme.colors.ink}; border-color: ${theme.colors.accentSoft};`}
    }
    &:active {
        transform: translateY(1px);
    }
`
